import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { SortingEnum } from '../enums/sorting-enum';
import { BlsGmbDifference } from '../../../models/locations-models/bls-gmb-difference';
import { ImportedLocationDelta, Location } from '../../../models/locations-models/location';
import { prepareForDiffChecking } from '../diff-utils';
import { combineLatestWith, fromEvent, mergeWith, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { DifferenceActions } from '../enums/difference-actions';
import { cloneDeep } from 'lodash';
import { isEqual } from 'lodash';
import { concatLatestFrom } from '@ngrx/operators';
import * as locationListSelectors from '../location-list/state/location-list-selectors';
import * as skeletonSelectors from '../state/locations-skeleton-selectors';
import { SkeletonBottomPanelActions } from '../state/locations-skeleton-actions';
import { LocationListApiService } from '../location-list/location-list-api.service';
import { LocationListLocalStoreService } from '../location-list/location-list-local-store.service';

export interface ColumnVisibilityModel {
  [DifferenceActions.updateGmb]: boolean, // essentially readonly -- the LSC column must always be visible
  [DifferenceActions.acceptDifference]: boolean,
  [DifferenceActions.acceptGoogleUpdateValue]: boolean,
}

export interface InfoMenuVisibilityModel {
  [DifferenceActions.updateGmb]: boolean,
  [DifferenceActions.acceptDifference]: boolean,
  [DifferenceActions.acceptGoogleUpdateValue]: boolean,
}

export interface BlsGmbDifferencesState {
  differences: BlsGmbDifference[];
  differencesCopy: BlsGmbDifference[];
  sorting: SortingEnum;
  rowsLoadingTranslations: string[];
  forceAnimation: boolean, // only purpose of this flag is so that the row animation gets triggered
  focusedRowStoreCode: string | undefined;
  blacklistingInProgress: string | null;
  coordinateRevalidationInProgress: boolean;
  continueToNextLocation: boolean;
  currentLocationIndex: number; // 1-based index of the currently shown location
  previousLocationIndex: number; // to determine from which direction should table rows be animated
  customPinsMinimized: boolean;
  isTableVerticallyOverflowing: boolean;
  columnVisibility: ColumnVisibilityModel,
  infoMenuVisibility: InfoMenuVisibilityModel
}

@Injectable()
export class BlsGmbDifferencesLocalStore extends ComponentStore<BlsGmbDifferencesState> {
  constructor(private store: Store,
              private locationListApi: LocationListApiService,
              private locationListLocalStore: LocationListLocalStoreService) {
    super({
      differences: [],
      differencesCopy: [],
      sorting: SortingEnum.ASCENDING,
      rowsLoadingTranslations: [],
      forceAnimation: false,
      focusedRowStoreCode: undefined,
      blacklistingInProgress: null,
      coordinateRevalidationInProgress: false,
      continueToNextLocation: false,
      currentLocationIndex: 1,
      previousLocationIndex: 1,
      customPinsMinimized: false,
      isTableVerticallyOverflowing: false,
      columnVisibility: {
        [DifferenceActions.updateGmb]: true,
        [DifferenceActions.acceptDifference]: true,
        [DifferenceActions.acceptGoogleUpdateValue]: true,
      },
      infoMenuVisibility: { // start off w/ the info menus turned off
        [DifferenceActions.updateGmb]: false,
        [DifferenceActions.acceptDifference]: false,
        [DifferenceActions.acceptGoogleUpdateValue]: false,
      },
    });

    this.initDifferenceViewModel(this.store.select(locationListSelectors.selectFocusedLocation));
    this.onResize(fromEvent(window, 'resize')
        .pipe(mergeWith(this.store.select(skeletonSelectors.selectContainerResizing))),
    );
    this.setIsUnsavedChange(this.differences$);
  }

  readonly differences$ = this.select((state) => state.differences);
  readonly differencesCopy$ = this.select((state) => state.differencesCopy);
  readonly continueToNextLocation$ = this.select((state) => state.continueToNextLocation);
  readonly focusedRowStoreCode$ = this.select((state) => state.focusedRowStoreCode);

  /*************************************************************************************************
   *                                    UPDATERS                                                   *
   ************************************************************************************************/


  updateDifferences = this.updater((state, differences: BlsGmbDifference[]): BlsGmbDifferencesState => ({
    ...state,
    differences,
  }));

  updateDifferencesCopy = this.updater((state, differences: BlsGmbDifference[]): BlsGmbDifferencesState => ({
    ...state,
    differencesCopy: cloneDeep(differences),
  }));

  updateDifference = this.updater((state, difference: BlsGmbDifference): BlsGmbDifferencesState => {
    return {
      ...state,
      differences: state.differences.map(diff => {
        if (diff.fieldId === difference.fieldId) {
          return difference;
        } else return diff;
      }),
    };
  });

  updateForceAnimation = this.updater((state, forceAnimation: boolean): BlsGmbDifferencesState => ({
    ...state,
    forceAnimation: forceAnimation,
  }));

  updateFocusedRowStoreCode = this.updater((state, storeCode: string): BlsGmbDifferencesState => ({
    ...state,
    focusedRowStoreCode: storeCode,
  }));

  updateBlacklistingInProgress = this.updater((state, blacklistingInProgress: string | null): BlsGmbDifferencesState => ({
    ...state,
    blacklistingInProgress,
  }));

  updateCoordinateRevalidationInProgress = this.updater((state, coordinateRevalidationInProgress: boolean): BlsGmbDifferencesState => ({
    ...state,
    coordinateRevalidationInProgress,
  }));

  updateCustomPinsMinimized = this.updater((state, customPinsMinimized: boolean): BlsGmbDifferencesState => ({
    ...state,
    customPinsMinimized,
  }));

  updateRowsLoadingTranslations = this.updater((state, fieldId: string): BlsGmbDifferencesState => {
    const rowsLoadingTranslationsCopy = [...state.rowsLoadingTranslations];
    const fieldIdIndex = rowsLoadingTranslationsCopy.indexOf(fieldId);
    if (fieldIdIndex !== -1) {
      rowsLoadingTranslationsCopy.splice(fieldIdIndex, 1);
    } else rowsLoadingTranslationsCopy.push(fieldId);
    return {
      ...state,
      rowsLoadingTranslations: rowsLoadingTranslationsCopy,
    };
  });

  updateSorting = this.updater((state, sorting: SortingEnum): BlsGmbDifferencesState => ({
    ...state,
    sorting,
  }));

  updateInfoMenuVisibility = this.updater((state, infoMenuVisibility: InfoMenuVisibilityModel): BlsGmbDifferencesState => ({
    ...state,
    infoMenuVisibility,
  }));

  updateColumnVisibility = this.updater((state, columnVisibility: ColumnVisibilityModel): BlsGmbDifferencesState => ({
    ...state,
    columnVisibility,
  }));

  updateIsTableVerticallyOverflowing = this.updater((state, isTableVerticallyOverflowing: boolean): BlsGmbDifferencesState => ({
    ...state,
    isTableVerticallyOverflowing,
  }));

  toggleContinueToNextLocation = this.updater((state): BlsGmbDifferencesState => ({
    ...state,
    continueToNextLocation: !state.continueToNextLocation,
  }));


  /*************************************************************************************************
   *                                       EFFECTS                                                 *
   ************************************************************************************************/


  readonly initDifferenceViewModel = this.effect((trigger$: Observable<Location>) => {
    return trigger$.pipe(
        concatLatestFrom(() => [this.locationListLocalStore.total$,
          this.continueToNextLocation$, this.focusedRowStoreCode$, this.differences$]),
        tap(([location, total, continueToNextLocation, focusedRowStoreCode, differences]) => {
          const newDifferences = [];
          const hasFocusedRowChanged = location?.storeCode !== focusedRowStoreCode;

          this.updateFocusedRowStoreCode(location?.storeCode);

          if (location) {
            for (const [fieldId, differenceDelta] of Object.entries((location as Location).differences)) {
              const diff = differences.find((diff: BlsGmbDifference) => diff.fieldId === fieldId);
              // if focused row is the same, preserve touched and translation props
              newDifferences.push(this.generateGmbBlsDifference(fieldId, differenceDelta,
                  !hasFocusedRowChanged && diff ? diff.touched : false,
                  !hasFocusedRowChanged && diff ? {
                    blsValueTranslated: diff.blsValueTranslated,
                    gmbValueTranslated: diff.gmbValueTranslated,
                    showTranslation: diff.showTranslation,
                  } : undefined));
            }
          }

          setTimeout(() => this.detectTableOverflow(), 200); // w8 till table is rendered

          this.updateDifferences(newDifferences);
          this.updateDifferencesCopy(newDifferences);
          // compute this only if focused row changed, fixes BLS-4848
          if (hasFocusedRowChanged) {
            this.determineContinueToNextLocation(location, newDifferences, total, continueToNextLocation);
            this.updateBlacklistingInProgress(null);
            this.updateCoordinateRevalidationInProgress(false);
            this.updateCustomPinsMinimized(false);
          }


          // only trigger animation if row changed
          if (hasFocusedRowChanged) {
            // setTimeout causes the animation to be jerky,
            // requestAnimationFrame() calls the provided callback before the next repaint
            // time this to give the animation a chance to register state change
            requestAnimationFrame(() => {
              // set animation state 'start'
              this.updateForceAnimation(true);
            });
          }

        }));
  });

  readonly onResize = this.effect((trigger$: Observable<any>) => {
    return trigger$.pipe(tap(() => {
      this.detectTableOverflow();
    }));
  });

  readonly setIsUnsavedChange = this.effect((trigger$: Observable<BlsGmbDifference[]>) => {
    return trigger$.pipe(combineLatestWith(this.differencesCopy$), tap(([differences, differencesCopy]) => {
      // relevant BlsGmbDifference props to determine unsaved changes
      const reducedDifferences = [];
      const reducedDifferencesCopy = [];
      differences.forEach((diff, i) => {
        reducedDifferences.push({
          acceptDifferencesSelected: diff?.acceptDifferencesSelected,
          gmbUpdateSelected: diff?.gmbUpdateSelected,
          acceptGoogleValueSelected: diff?.acceptGoogleValueSelected,
        });
        reducedDifferencesCopy.push({
          acceptDifferencesSelected: differencesCopy[i]?.acceptDifferencesSelected,
          gmbUpdateSelected: differencesCopy[i]?.gmbUpdateSelected,
          acceptGoogleValueSelected: differencesCopy[i]?.acceptGoogleValueSelected,
        });
      });

      this.store.dispatch(SkeletonBottomPanelActions.setUnsavedChangesInBottomPanelTab({
        unsavedChangesInBottomPanelTab:
            !isEqual(reducedDifferences, reducedDifferencesCopy),
      }));
    }));
  });


  /*************************************************************************************************
   *                                          HELPERS                                              *
   ************************************************************************************************/


  /**
   * Generates a GmbBlsDifference object
   * @param fieldId
   * @param differenceDelta
   * @param _touched
   * @param _translationConfig already loaded translation
   */
  private generateGmbBlsDifference(
      fieldId: string,
      differenceDelta: ImportedLocationDelta,
      _touched = false,
      _translationConfig?: { blsValueTranslated: string, gmbValueTranslated: string, showTranslation: boolean }): BlsGmbDifference {
    return {
      fieldId: fieldId,
      showTranslation: _translationConfig ? _translationConfig.showTranslation : false,
      touched: _touched,
      ignored: differenceDelta.ignored,

      blsValue: prepareForDiffChecking(fieldId, differenceDelta.importedLocationValue),
      gmbValue: prepareForDiffChecking(fieldId, differenceDelta.myBusinessValue),
      blsValueTranslated: _translationConfig ? _translationConfig.blsValueTranslated :
          prepareForDiffChecking(fieldId, differenceDelta.importedLocationValueTranslated),
      gmbValueTranslated: _translationConfig ? _translationConfig.gmbValueTranslated :
          prepareForDiffChecking(fieldId, differenceDelta.myBusinessValueTranslated),
      // mimicking `gmbValue` for now -- TODO fix me
      googleUpdateValue: prepareForDiffChecking(fieldId, differenceDelta.myBusinessValue),

      gmbUpdateSelected: false,
      acceptDifferencesSelected: differenceDelta.ignored,
      // TODO replace me when google update is done
      acceptGoogleValueSelected: false,
    };
  }

  /**
   * Detects if table is vertically overflowing. Used to apply right padding to footer and
   * header row when a vert. scroll bar is rendered on the table. Added in BLS-3072.
   */
  private detectTableOverflow() {
    const diffTable = document.getElementById('differenceTable');

    if (!diffTable) {
      return;
    }

    this.updateIsTableVerticallyOverflowing(diffTable.scrollHeight >
        diffTable.clientHeight);
  }

  /**
   * Uncheck continueToNextLocation if user reached the end of the list and continueToNextLocation
   * had been checked
   * @param location
   * @param differences
   * @param total
   * @param continueToNextLocation
   */
  private determineContinueToNextLocation(location: Location,
                                          differences: BlsGmbDifference[],
                                          total: number,
                                          continueToNextLocation: boolean) {

    if (!location || !differences.length) {
      if (continueToNextLocation) {
        this.toggleContinueToNextLocation();
        return;
      }
      return;
    }

    if ((total - 1) === this.locationListApi.getRowById(location?.id?.toString())?.rowIndex && // we're at the last row
        continueToNextLocation) {
      this.toggleContinueToNextLocation();
    }
  }


}
